import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { FileText, UploadSimple, Trash, DownloadSimple, EyeSlash, Eye, NotePencil } from "@phosphor-icons/react";
import { Section, Pill, fmtD, fmtDT, fmtBytes, err, EXPIRY_TONE } from "./hrUtils";
import { Sel, F } from "./EmployeeCards";
import { TONE_CLASSES } from "../assets/assetUtils";

const openDoc = async (d) => {
  try {
    const r = await API.get(`/hr/documents/${d.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) { err(e, "Couldn't open that document"); }
};

/** Documents with category, expiry and employee visibility. HR uploads anything; the person uploads their own ID / RTW / qualifications. */
export function DocumentsCard({ emp, meta, hr, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ category: "", name: "", expires_on: "", visible: true, notes: "" });
  const fileRef = useRef();
  const cats = (meta?.doc_categories || []).filter((c) => hr || (meta?.self_upload_categories || []).includes(c.key));
  const docs = emp.documents || [];

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a file"); return; }
    if (!f.category) { toast.error("Pick a category"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", f.category);
    fd.append("name", f.name);
    fd.append("expires_on", f.expires_on);
    fd.append("notes", f.notes);
    if (hr) fd.append("visible_to_employee", f.visible ? "true" : "false");
    setBusy(true);
    try {
      await API.post(`/hr/employees/${emp.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setOpen(false);
      setF({ category: "", name: "", expires_on: "", visible: true, notes: "" });
      onSaved?.();
    } catch (e) { err(e, "Upload failed"); } finally { setBusy(false); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Remove "${d.name}"?`)) return;
    try { await API.delete(`/hr/documents/${d.id}`); toast.success("Document removed"); onSaved?.(); } catch (e) { err(e, "Couldn't remove"); }
  };
  const toggleVis = async (d) => {
    try { await API.put(`/hr/documents/${d.id}`, { visible_to_employee: !d.visible_to_employee }); onSaved?.(); } catch (e) { err(e, "Couldn't update"); }
  };

  return (
    <Section title={`Documents (${docs.length})`} icon={FileText} testid="hr-card-documents" action={<Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen(true)} data-testid="hr-doc-upload-btn"><UploadSimple size={13} className="mr-1" /> Upload</Button>}>
      {docs.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="hr-docs-empty">No documents yet — contracts, right-to-work evidence, ID, qualifications, DBS and licences live here.</p> : (
        <ul className="divide-y divide-border">
          {docs.map((d) => (
            <li key={d.id} className="py-2 first:pt-0 last:pb-0 flex flex-wrap items-center gap-2 text-sm" data-testid={`hr-doc-${d.id}`}>
              <button onClick={() => openDoc(d)} className="flex-1 min-w-[200px] text-left hover:underline" data-testid={`hr-doc-open-${d.id}`}>
                <p className="font-medium leading-tight">{d.name}</p>
                <p className="text-[11px] text-muted-foreground">{d.category_label} · {fmtBytes(d.size)} · {d.uploaded_by}{d.self_uploaded ? " (self)" : ""} · {fmtDT(d.uploaded_at)}</p>
              </button>
              {d.expires_on && <Pill tone={EXPIRY_TONE[d.expiry_state]} testid={`hr-doc-expiry-${d.id}`}>{d.expiry_state === "expired" ? "Expired" : "Expires"} {fmtD(d.expires_on)}</Pill>}
              {hr && <button onClick={() => toggleVis(d)} title={d.visible_to_employee ? "Visible to the employee — click to hide" : "HR only — click to share"} className="text-muted-foreground hover:text-foreground" data-testid={`hr-doc-vis-${d.id}`}>{d.visible_to_employee ? <Eye size={15} /> : <EyeSlash size={15} />}</button>}
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openDoc(d)} aria-label="Download" data-testid={`hr-doc-download-${d.id}`}><DownloadSimple size={14} /></Button>
              {(hr || d.self_uploaded) && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => remove(d)} aria-label="Remove" data-testid={`hr-doc-delete-${d.id}`}><Trash size={14} /></Button>}
            </li>
          ))}
        </ul>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="hr-doc-dialog">
          <DialogHeader><DialogTitle>Upload a document</DialogTitle><DialogDescription>PDF, image or office file up to 25 MB. {hr ? "Choose whether the person can see it." : "HR is told when you add something."}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">File</Label><input ref={fileRef} type="file" className="mt-1 block w-full text-sm" data-testid="hr-doc-file" /></div>
            <F label="Category"><Sel value={f.category} onChange={(v) => setF({ ...f, category: v, visible: cats.find((c) => c.key === v)?.self_visible ?? true })} testid="hr-doc-category"><option value="">Choose…</option>{cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</Sel></F>
            <F label="Name (optional)"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Defaults to the file name" className="mt-1" data-testid="hr-doc-name" /></F>
            <F label="Expires on (optional)"><Input type="date" value={f.expires_on} onChange={(e) => setF({ ...f, expires_on: e.target.value })} className="mt-1" data-testid="hr-doc-expires" /></F>
            {hr && <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.visible} onChange={(e) => setF({ ...f, visible: e.target.checked })} data-testid="hr-doc-visible" /> Visible to {emp.display_name} in My HR</label>}
            <F label="Notes"><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className="mt-1" /></F>
          </div>
          <DialogFooter className="gap-2 sm:gap-0"><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={upload} disabled={busy} data-testid="hr-doc-submit">{busy ? "Uploading…" : "Upload"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

const KIND_LABEL = { note: "Note", meeting: "1:1 / meeting", performance: "Performance", disciplinary: "Disciplinary", wellbeing: "Wellbeing", praise: "Praise" };
const KIND_TONE = { disciplinary: TONE_CLASSES.overdue, performance: TONE_CLASSES.soon, praise: TONE_CLASSES.ok, wellbeing: TONE_CLASSES.none, meeting: TONE_CLASSES.none, note: TONE_CLASSES.none };

/** HR-only confidential timeline. */
export function NotesCard({ emp }) {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState("");
  const [kind, setKind] = useState("note");
  const load = () => API.get(`/hr/employees/${emp.id}/notes`).then((r) => setNotes(r.data.notes)).catch(() => setNotes([]));
  useEffect(() => { load(); }, [emp.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => {
    if (!text.trim()) return;
    try { await API.post(`/hr/employees/${emp.id}/notes`, { text, kind }); setText(""); load(); } catch (e) { err(e, "Couldn't add the note"); }
  };
  const del = async (n) => { try { await API.delete(`/hr/notes/${n.id}`); load(); } catch (e) { err(e, "Couldn't delete"); } };
  return (
    <Section title="HR notes (confidential)" icon={NotePencil} testid="hr-card-notes">
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <Sel value={kind} onChange={setKind} testid="hr-note-kind">{Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Sel>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Record a 1:1, a conversation, a concern or a well done. Only HR can read these." className="flex-1" data-testid="hr-note-text" />
        <Button onClick={add} disabled={!text.trim()} className="self-end" data-testid="hr-note-add">Add</Button>
      </div>
      {notes === null ? null : notes.length === 0 ? <p className="text-xs text-muted-foreground">No notes yet.</p> : (
        <ul className="divide-y divide-border">{notes.map((n) => (
          <li key={n.id} className="py-2 text-sm" data-testid={`hr-note-${n.id}`}>
            <div className="flex items-center justify-between gap-2"><div className="flex items-center gap-2"><Pill tone={KIND_TONE[n.kind]}>{KIND_LABEL[n.kind] || n.kind}</Pill><span className="text-[11px] text-muted-foreground">{n.by} · {fmtDT(n.at)}</span></div><button onClick={() => del(n)} className="text-muted-foreground hover:text-red-600" aria-label="Delete note" data-testid={`hr-note-delete-${n.id}`}><Trash size={13} /></button></div>
            <p className="mt-1 whitespace-pre-wrap">{n.text}</p>
          </li>
        ))}</ul>
      )}
    </Section>
  );
}
